import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  { value: "sell",  label: "I want to SELL"  },
  { value: "trade", label: "I want to TRADE" },
  { value: "buy",   label: "I'm looking to BUY" },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

export default function TradeForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    intent: "sell",
    card_details: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      toast.error("Image too large. Max 5MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
    try {
      const url = await fileToDataUrl(f);
      setPreview(url);
    } catch {
      setPreview(null);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.card_details) {
      toast.error("Please fill out name, email, and card details.");
      return;
    }
    setSubmitting(true);
    try {
      let image_base64 = null;
      if (file) image_base64 = await fileToDataUrl(file);
      await axios.post(`${API}/trade-submissions`, { ...form, image_base64 });
      toast.success("Submission received! We'll be in touch shortly.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", intent: "sell", card_details: "" });
      setFile(null);
      setPreview(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="trade"
      data-testid="trade-section"
      className="bg-white border-b-4 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="tag-chip tag-chip-red" data-testid="trade-eyebrow">
              /// Buy · Sell · Trade
            </span>
            <h2
              data-testid="trade-heading"
              className="mt-6 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              Got cards? <br />
              <span className="text-[#FF1F1F]">Let&apos;s deal.</span>
            </h2>
            <p className="mt-6 text-lg text-black/80 leading-relaxed max-w-md">
              Send us a quick rundown and a photo. We&apos;ll review and reply with
              an honest offer or a trade proposal. No pressure, no lowballs.
            </p>

            <ul className="mt-8 space-y-3 text-base">
              {[
                "Fair, market-based offers",
                "Cash, trade credit, or cards in return",
                "Quick reply — usually within 24 hours",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#FF1F1F] mt-1 shrink-0" size={20} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="trade-form"
              className="nb-card p-6 md:p-8 bg-white"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Your Name
                  </label>
                  <input
                    data-testid="trade-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Ash Ketchum"
                    className="nb-input"
                    required
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Email
                  </label>
                  <input
                    data-testid="trade-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@email.com"
                    className="nb-input"
                    required
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Phone (optional)
                  </label>
                  <input
                    data-testid="trade-input-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="(555) 010-7878"
                    className="nb-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Intent
                  </label>
                  <select
                    data-testid="trade-input-intent"
                    name="intent"
                    value={form.intent}
                    onChange={onChange}
                    className="nb-select"
                  >
                    {INTENTS.map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                  Card Details
                </label>
                <textarea
                  data-testid="trade-input-details"
                  name="card_details"
                  rows={5}
                  value={form.card_details}
                  onChange={onChange}
                  placeholder="Set / card name / condition / grading info / what you're looking for in return…"
                  className="nb-input"
                  required
                />
              </div>

              <div className="mt-5">
                <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                  Upload Card Photo (optional, max 5MB)
                </label>
                <label className="nb-file flex items-center gap-3 cursor-pointer" data-testid="trade-file-label">
                  <Upload size={22} />
                  <span className="flex-1">
                    {file ? file.name : "Click to choose a photo of your card(s)"}
                  </span>
                  <input
                    data-testid="trade-input-file"
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                  />
                </label>
                {preview && (
                  <div className="mt-4 inline-block border-4 border-black p-1 bg-white">
                    <img
                      data-testid="trade-image-preview"
                      src={preview}
                      alt="Card preview"
                      className="max-h-48 object-contain"
                    />
                  </div>
                )}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  data-testid="trade-submit-button"
                  className="nb-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} /> Sending…
                    </>
                  ) : (
                    "Submit For Review"
                  )}
                </button>
                {done && (
                  <span
                    data-testid="trade-success-msg"
                    className="font-mono text-xs uppercase tracking-widest font-bold text-[#FF1F1F] inline-flex items-center gap-2"
                  >
                    <CheckCircle2 size={18} />
                    Submission received. We&apos;ll be in touch.
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
